import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { CancelPackageDialog } from "@/shared/ui/CancelPackageDialog";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  useCancelPrivateLessonPackage,
  usePrivateLessonPackagesQuery,
} from "../api/queries";
import { PackagesFilter } from "../ui/PackagesFilter";
import { PackagesTable } from "../ui/PackagesTable";
import { PackagesToolbar } from "../ui/PackagesToolbar";

export function PrivateLessonPackagesPage() {
  const nav = useNavigate({ from: "/app/private-lessons/packages" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/private-lessons/packages",
  });

  const query = useMemo<OffsetPageQueryDTO>(
    () => ({
      limit: search.limit ?? 20,
      offset: search.offset ?? 0,
      sort: search.sort,
      filters: search.filters,
    }),
    [search.limit, search.offset, search.sort, search.filters],
  );

  const list = usePrivateLessonPackagesQuery(query);
  const canCreate = useActionPermission(["private-lessons:sell"]);
  const cancelPackage = useCancelPrivateLessonPackage();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null);

  const cancelTargetPackage = useMemo(
    () =>
      list.data?.data?.find((p) => p.id === cancelTargetId),
    [list.data?.data, cancelTargetId],
  );

  return (
    <>
      <CancelPackageDialog
        open={cancelDialogOpen}
        onOpenChange={(open) => {
          setCancelDialogOpen(open);
          if (!open) setCancelTargetId(null);
        }}
        packageLabel={cancelTargetPackage?.templateName}
        onConfirm={async (cancelReason) => {
          if (!cancelTargetId) return;
          await cancelPackage.mutateAsync({
            id: cancelTargetId,
            cancelReason,
          });
          setCancelDialogOpen(false);
          setCancelTargetId(null);
        }}
      />
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ແພັກເກດຮຽນສ່ວນຕົວ" },
          ]}
        />
      </Header>

      <Main>
        <PackagesToolbar
          canManage={!!canCreate}
          onCreate={canCreate ? () => nav({ to: "/app/sell" }) : undefined}
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <PackagesFilter />

          <PackagesTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onView={(pkg) => {
              nav({
                to: "/app/private-lessons/packages/$packageId" as any,
                params: { packageId: pkg.id },
              });
            }}
            onCancel={(id) => {
              setCancelTargetId(id);
              setCancelDialogOpen(true);
            }}
          />
        </div>
      </Main>
    </>
  );
}
